import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { type ReactNode, useId } from 'react';

import { formatNumber } from '../../lib/numbers';
import { visuallyHidden } from '../../lib/visually-hidden';
import { cssVar } from '../../theme/tokens';
import { type ControlSize, iconSize, mergeSx } from './sizes';

export interface SegmentedOption<V extends string = string> {
  value: V;
  /** Visible text; also the accessible name when `iconOnly` hides it. */
  label: string;
  icon?: ReactNode;
  /** Show only the icon; the label stays as the accessible name and the title. */
  iconOnly?: boolean;
  disabled?: boolean;
  /** A small figure after the label (items with that status); read as "Live, 2". */
  count?: number;
}

export interface SegmentedControlProps<V extends string = string> {
  /** Accessible name of the group ("Date range", "Theme"). */
  label: string;
  options: readonly SegmentedOption<V>[];
  value: V;
  onChange: (value: V) => void;
  size?: ControlSize;
  /** Stretch the segments to fill the width. */
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * One choice from a few options as a row of segments on a track (date ranges, formats, the
 * theme). Native radio buttons under the segments: Tab reaches the selected one and the arrow
 * keys move and select, as in any radio group. The track is as tall as a control of the same
 * size, so it lines up with buttons.
 */
export function SegmentedControl<V extends string = string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
  fullWidth = false,
  sx,
}: SegmentedControlProps<V>) {
  const name = useId();
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={mergeSx(
        {
          display: fullWidth ? 'flex' : 'inline-flex',
          gap: cssVar('space-half'),
          height: cssVar(`control-h-${size}`),
          p: cssVar('space-half'),
          borderRadius: cssVar('control-radius'),
          bgcolor: cssVar('track'),
          boxSizing: 'border-box',
          maxWidth: '100%',
          overflowX: 'auto',
        },
        sx,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            key={option.value}
            component="label"
            title={option.iconOnly ? option.label : undefined}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: fullWidth ? 1 : 'none',
              gap: 1.5,
              height: '100%',
              minWidth: `calc(${cssVar(`control-h-${size}`)} - ${cssVar('space-1')})`,
              px: option.iconOnly ? 0 : 3.5,
              borderRadius: `calc(${cssVar('control-radius')} - ${cssVar('space-half')})`,
              typography: 'bodySm',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              cursor: option.disabled ? 'default' : 'pointer',
              opacity: option.disabled ? 0.5 : 1,
              color: selected ? cssVar('text') : cssVar('text-muted'),
              bgcolor: selected ? cssVar('segment-selected') : 'transparent',
              boxShadow: selected ? cssVar('shadow-sm') : 'none',
              '&:hover': { color: option.disabled ? undefined : cssVar('text') },
              '&:has(input:focus-visible)': {
                outline: `2px solid ${cssVar('focus-ring')}`,
                outlineOffset: 2,
              },
              '& .MuiSvgIcon-root': { fontSize: iconSize(size) },
            }}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              disabled={option.disabled}
              aria-label={option.iconOnly ? option.label : undefined}
              onChange={() => onChange(option.value)}
              style={visuallyHidden}
            />
            {option.icon}
            {option.iconOnly ? null : option.label}
            {option.count === undefined ? null : (
              <>
                <span style={visuallyHidden}>, {formatNumber(option.count)}</span>
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    typography: 'caption',
                    fontWeight: 700,
                    color: cssVar('text-subtle'),
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatNumber(option.count)}
                </Box>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
